/**
 * The shape of saved player data (tasks 9.4, 9.5, 9.8, 13.2).
 *
 * One document holds every save profile on the device. It is plain serialisable
 * JSON for the same reason `GameState` is: it is written to storage, exported to
 * a file, and read back by a later version of the application.
 *
 * The in-progress run lives *inside* the document rather than beside it, which
 * is what makes export self-sufficient — a profile exported from one device
 * resumes on another, mid-proposal, because the whole run travelled with it
 * (`save-system` spec, "Round trip across devices").
 */
import type { Action, GameState } from '@managing-up/engine';
import type { ShellSettings } from '../settings.js';

/**
 * Bumped whenever `SaveDocument` changes shape and a migration is needed.
 *
 * 1 → 2: mission results gained `completed`, `cxpAwarded`, `days` and `sends`;
 * profiles gained `onboarding`; the in-progress run went from a title-screen
 * summary to a resumable record.
 *
 * 2 → 3: settings gained `messageSpace`, the split between the message feed and
 * the composer dock.
 *
 * 3 → 4: profiles gained `tutorialsCompleted`, the guided walkthroughs this
 * player has walked to the end.
 *
 * 4 → 5: settings gained `colour`, whether the expressive palette is applied,
 * and `motion` admitted a fourth value. Only the first of those is a shape
 * change — widening the permitted values of an existing setting rewrites
 * nothing, which is the point of `calm` being additive.
 */
export const SAVE_VERSION = 5;

/** A finished run's result at one difficulty. */
export interface MissionResult {
  readonly grade: string;
  readonly score: number;
  /** True when the run used a mid-mission difficulty switch, which caps the grade. */
  readonly assisted: boolean;
  /** False for a recorded failure: a mission can be scored without being cleared. */
  readonly completed: boolean;
  /**
   * Corporate Experience this result was worth. Banked as the *improvement* on
   * the previous best at this difficulty, so replaying a cleared mission pays
   * the difference rather than the whole award again.
   */
  readonly cxpAwarded: number;
  /** Days the run took, for the fastest-completion record. */
  readonly days: number;
  readonly sends: number;
}

/** Everything known about one mission for one save profile. */
export interface MissionProgress {
  readonly missionId: string;
  /** Best result per difficulty profile id. Absent means never played there. */
  readonly best: Readonly<Record<string, MissionResult>>;
}

/**
 * Enough of an in-progress run to describe it on the title screen without
 * loading a package: what Continue says on it.
 */
export interface RunSummary {
  readonly runKey: string;
  readonly missionId: string;
  readonly missionTitle: string;
  readonly packVersion: string;
  /** The difficulty profile the run is being played at, after any switch. */
  readonly profileId: string;
  readonly profileLabel: string;
  readonly day: number;
  readonly phase: string;
  /** ISO 8601, device local. Cosmetic: used only to order and describe saves. */
  readonly updatedAt: string;
}

/**
 * A run that can actually be resumed (task 13.2).
 *
 * The state *and* the action log are both kept. The state is what resume
 * restores — restoring by replaying the log would be slower and would diverge
 * the moment an engine version changed — and the log is what makes a run
 * reproducible from a bug report (task 13.8). They are written together after
 * every state-changing action, so a browser killed without a clean shutdown
 * loses at most the action that was mid-flight.
 *
 * `engineVersion` and `stateVersion` are recorded rather than assumed: a state
 * written by a different engine is not resumed, because the shapes agreeing is
 * not the same as the rules agreeing.
 */
export interface SavedRun extends RunSummary {
  readonly packId: string;
  /**
   * Where the package was loaded from, relative to the catalog. Published
   * packages are immutable and permanently addressable, so this is what makes
   * "resume on the cached original" possible after the catalog has moved on
   * (`save-system` spec, "Content version drift handling").
   */
  readonly packUrl: string;
  readonly engineVersion: string;
  readonly stateVersion: number;
  readonly seed: string;
  /** Everything the profile contributed at `createGame`, for an exact replay. */
  readonly parPhases: number | null;
  readonly equippedPerkIds: readonly string[];
  readonly state: GameState;
  /** Every action dispatched, in order. */
  readonly history: readonly Action[];
}

export interface CareerState {
  /** Corporate Experience banked and not yet spent. */
  readonly cxp: number;
  readonly ownedPerkIds: readonly string[];
  readonly equippedPerkIds: readonly string[];
}

export interface SaveProfile {
  readonly id: string;
  readonly name: string;
  readonly createdAt: string;
  readonly settings: ShellSettings;
  readonly progress: readonly MissionProgress[];
  readonly career: CareerState;
  /**
   * Onboarding prompts this player has dismissed, by id. Sorted. Kept on the
   * save profile rather than the device because the prompts teach *this
   * player*: a second person on the same laptop has not been taught anything.
   */
  readonly onboarding: readonly string[];
  /**
   * Missions whose guided walkthrough this player has walked to its final step,
   * by mission id. Sorted.
   *
   * Deliberately not a `MissionProgress` entry: a tutorial holds no record, no
   * grade and no score (`scoring-progression` spec), and a completion recorded
   * the ordinary way would count toward completed-mission totals and satisfy
   * unlock prerequisites. What is worth remembering is only that the
   * walkthrough taught this player — which is what retires the contextual
   * onboarding cards (design D5) and what mission select reports.
   *
   * Leaving a walkthrough early records nothing, so this is a list of
   * completions rather than of attempts.
   */
  readonly tutorialsCompleted: readonly string[];
  /** At most one in-progress run per save profile. */
  readonly run: SavedRun | null;
}

export interface SaveDocument {
  readonly saveVersion: number;
  readonly activeProfileId: string;
  readonly profiles: readonly SaveProfile[];
}

export const EMPTY_CAREER: CareerState = { cxp: 0, ownedPerkIds: [], equippedPerkIds: [] };

/**
 * Mission ids *cleared* at any difficulty — what unlock prerequisites read.
 *
 * A recorded failure is a result but not a completion, so losing mission 02
 * badly enough to score does not unlock mission 03.
 */
export function completedMissionIds(profile: SaveProfile): readonly string[] {
  return profile.progress
    .filter((entry) => Object.values(entry.best).some((result) => result.completed))
    .map((entry) => entry.missionId);
}

/** Whether this player has walked a mission's guided walkthrough to its end. */
export function hasCompletedTutorial(profile: SaveProfile, missionId: string): boolean {
  return profile.tutorialsCompleted.includes(missionId);
}

export function progressFor(profile: SaveProfile, missionId: string): MissionProgress | null {
  return profile.progress.find((entry) => entry.missionId === missionId) ?? null;
}

/** The resumable run for a mission, or `null` when the profile has none. */
export function runFor(profile: SaveProfile, missionId: string): SavedRun | null {
  return profile.run?.missionId === missionId ? profile.run : null;
}

export function activeProfileOf(doc: SaveDocument): SaveProfile {
  const found = doc.profiles.find((profile) => profile.id === doc.activeProfileId);
  if (found) return found;
  const first = doc.profiles[0];
  if (!first) throw new Error('A save document always holds at least one profile.');
  return first;
}
